import { inject, isDevMode, Signal } from '@angular/core';

import { SchemaDataSourceHost } from '../data-source/schema-data-source';
import { DataState } from '../model/data-state';
import { SCHEMA_CONTEXT } from '../model/schema-context';
import { contextRoot } from './roots/context.root';
import { dataRoot } from './roots/data.root';
import { SchemaRootResolver, SchemaScope } from './roots/schema-root';
import { sourceRoot } from './roots/source.root';
import { stateRoot } from './roots/state.root';
import {
  findSchemaPlaceholders,
  parseSchemaAddress,
  replaceSchemaPlaceholders,
  SCHEMA_ADDRESS_ROOTS,
  SchemaAddress,
  SchemaAddressRoot,
} from './schema-address';
import { SchemaDataRead, SchemaResolver } from './schema-resolver.types';
import { SCHEMA_STATE } from './schema-state';

/**
 * One resolver per root, typed over the root table: a root added there without a resolver does not compile, and this map
 * can hold nothing the table does not name. A new root is a file in `roots/` and a line here.
 */
const ROOT_RESOLVERS: Record<SchemaAddressRoot, SchemaRootResolver> = {
  state: stateRoot,
  data: dataRoot,
  context: contextRoot,
  source: sourceRoot,
};

const ROOT_LIST = SCHEMA_ADDRESS_ROOTS.map((root) => `$${root}`).join(', ');

/**
 * The resolver for this place — the one way to read what a schema names (README-TECH §13).
 *
 * `state` is what `$data` and `$state` read. A component passes its own `state` input, which holds in a spec that sets the
 * input as much as under an outlet. An action and a data source pass nothing and read the `SCHEMA_STATE` their place
 * bound: the node that fired the action, the state a source's node was handed.
 *
 * The context and the source are read per call, inside whatever `computed` the caller is in, and only by the root that
 * names them, so a `$data` read tracks neither.
 */
export function injectSchemaResolver(state?: Signal<DataState | undefined>): SchemaResolver {
  const bound = state ?? inject(SCHEMA_STATE, { optional: true });
  const context = inject(SCHEMA_CONTEXT, { optional: true });
  const host = inject(SchemaDataSourceHost, { optional: true });

  const scopeOf = (override: readonly (DataState | undefined)[]): SchemaScope => ({
    state: () => (override.length > 0 ? override[0] : bound?.()),
    context: () => context?.(),
    source: () => sourceState(host),
  });

  return {
    read: (address, ...override) => readField(address, scopeOf(override)),
    resolve: (value, ...override) => resolveValue(value, scopeOf(override)),
    dataOf: (value) => dataOf(value),
  };
}

/** The address of the data a read goes through — what the condition guard reads to tell "no data yet" apart. */
export function dataAddressOf(read: SchemaDataRead): string {
  return read.from === 'node' ? '$data' : '$source.data';
}

/** `undefined` where no producer is declared anywhere above, `null` while the nearest one is still connecting. */
function sourceState(host: SchemaDataSourceHost | null): DataState | null | undefined {
  if (!host?.hasProducer()) {
    return undefined;
  }

  return host.dataSource()?.state() ?? null;
}

function readField(address: unknown, scope: SchemaScope): unknown {
  if (address === undefined || address === null || address === '') {
    return undefined;
  }

  const parsed = typeof address === 'string' ? parseSchemaAddress(address) : null;

  if (!parsed) {
    reportNotAnAddress(address);

    return undefined;
  }

  return readAddress(parsed, scope);
}

function resolveValue(value: unknown, scope: SchemaScope): unknown {
  if (typeof value === 'string') {
    const address = parseSchemaAddress(value);

    if (address) {
      return readAddress(address, scope);
    }

    return replaceSchemaPlaceholders(value, (placeholder) => String(readAddress(placeholder, scope) ?? ''));
  }

  if (Array.isArray(value)) {
    return value.map((item) => resolveValue(item, scope));
  }

  if (isPlainObject(value)) {
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, resolveValue(item, scope)]));
  }

  return value;
}

function dataOf(value: unknown): readonly SchemaDataRead[] {
  if (typeof value !== 'string') {
    return [];
  }

  const address = parseSchemaAddress(value);

  return (address ? [address] : findSchemaPlaceholders(value)).flatMap((each) => {
    const read = rootOf(each.root)?.dataOf?.(each.keys);

    return read ? [read] : [];
  });
}

function readAddress(address: SchemaAddress, scope: SchemaScope): unknown {
  const root = rootOf(address.root);

  if (!root) {
    reportUnknownRoot(address);

    return undefined;
  }

  return root.resolve(address, scope);
}

/** By own key, so a name every object inherits — `$constructor`, `$toString` — is not taken for a root. */
function rootOf(name: string): SchemaRootResolver | undefined {
  return Object.hasOwn(ROOT_RESOLVERS, name) ? ROOT_RESOLVERS[name as SchemaAddressRoot] : undefined;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && Object.getPrototypeOf(value) === Object.prototype;
}

/** Each is a schema-authoring bug an end user can do nothing about: named in dev mode, silent in production. */
function reportUnknownRoot(address: SchemaAddress): void {
  if (isDevMode()) {
    console.warn(`Schema address "${address.text}" starts with "$${address.root}", which is not a root. An address may name ${ROOT_LIST}.`);
  }
}

function reportNotAnAddress(value: unknown): void {
  if (isDevMode()) {
    console.warn(
      `"${String(value)}" is in a field that is an address, but it is not one, so it resolves to nothing. ` +
        `An address names its root: ${ROOT_LIST}.`
    );
  }
}
